<template>
  <div class="format-content-grid">
    
    <!-- General League Structure -->
    <div class="format-card overview-card">
      <div class="card-header">
        <Icon name="mdi:trophy-award" />
        <h3>General League Structure</h3>
      </div>
      <div class="card-content">
        <div class="highlight-box">
          <Icon name="mdi:certificate" />
          <div>
            <h4>BCAPL Sanctioned Ca$h League</h4>
            <p>Official league with standardized rules ensuring fair and competitive play across all formats.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Mosconi Format Team Play -->
    <div class="format-card">
      <div class="card-header">
        <Icon name="mdi:billiards" />
        <h3>Mosconi Format Team Play</h3>
        <div class="format-badge">3x3, 4x</div>
      </div>
      <div class="card-content">
        
        <div class="format-subsection">
          <div class="subsection-header">
            <Icon name="mdi:account" />
            <h4>Singles Play</h4>
          </div>
          <p>A player and opponent play a single game without any collaboration from anyone.</p>
        </div>
        
        <div class="format-subsection">
          <div class="subsection-header">
            <Icon name="mdi:cue-stick" />
            <h4>Break Rules</h4>
          </div>
          <p>Break is decided by the scoring app. If you intend to soft break, you must notify your opponent. The only exception is the team scotch match where the team chooses an eligible member to lag.</p>
        </div>
        
      </div>
    </div>

    <!-- Scotch Doubles -->
    <div class="format-card">
      <div class="card-header">
        <Icon name="mdi:account-group" />
        <h3>Scotch Doubles</h3>
      </div>
      <div class="card-content">
        
        <div class="rules-grid">
          <div class="rule-item">
            <Icon name="mdi:account-multiple" />
            <div class="rule-content">
              <h4>Team Structure</h4>
              <p>Two-player teams where players alternate shots within their team's turn at the table.</p>
            </div>
          </div>
          
          <div class="rule-item">
            <Icon name="mdi:play-circle" />
            <div class="rule-content">
              <h4>Breaking</h4>
              <p>Either player on the team may break. The team can discuss who is best suited to take the break shot.</p>
            </div>
          </div>
          
          <div class="rule-item">
            <Icon name="mdi:swap-horizontal" />
            <div class="rule-content">
              <h4>Alternating Shots</h4>
              <p>After the break, team members must alternate shots for the remainder of their inning. Failure to do so is a foul.</p>
            </div>
          </div>
          
          <div class="rule-item">
            <Icon name="mdi:chat" />
            <div class="rule-content">
              <h4>Coaching Rules</h4>
              <p>Coaching is permitted between team members during their inning. Limited to one or two sentences said in passing. Longer conversations require a timeout token.</p>
            </div>
          </div>
        </div>
        
      </div>
    </div>

    <!-- Scoring System -->
    <div class="format-card scoring-card">
      <div class="card-header">
        <Icon name="mdi:chart-line" />
        <h3>Scoring System</h3>
      </div>
      <div class="card-content">
        
        <div class="scoring-info">
          <Icon name="mdi:cellphone" />
          <p>Scoring is done using the <strong>BCAPL Scoring app</strong></p>
        </div>
        
        <div class="scoring-types">
          <div class="scoring-type">
            <div class="type-header">
              <Icon name="mdi:numeric-8-circle" />
              <h4>8 Ball Scoring</h4>
            </div>
            <div class="scoring-rules">
              <div class="score-rule">
                <span class="points">10 points</span>
                <span class="description">for a win</span>
              </div>
              <div class="score-rule">
                <span class="points">1 point</span>
                <span class="description">for each ball from the loser's suit not remaining on the table</span>
              </div>
            </div>
          </div>
          
          <div class="scoring-type">
            <div class="type-header">
              <Icon name="mdi:numeric-9-circle" />
              <h4>9 Ball Scoring</h4>
            </div>
            <div class="scoring-rules">
              <div class="score-rule">
                <span class="points">10 points</span>
                <span class="description">for the win</span>
              </div>
              <div class="score-rule">
                <span class="points">5 points</span>
                <span class="description">for the loser (if one ball is pocketed)</span>
              </div>
              <div class="score-rule">
                <span class="points">0 points</span>
                <span class="description">if shut out</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="rules-note">
          <Icon name="mdi:information" />
          <p>Complete BCAPL Pool League Rules apply with one exception: timeouts are allowed per instructions below.</p>
        </div>
        
      </div>
    </div>

    <!-- Timeouts -->
    <div class="format-card timeouts-card">
      <div class="card-header">
        <Icon name="mdi:timer" />
        <h3>Timeout Rules</h3>
        <div class="timeout-badge">3 Per Match</div>
      </div>
      <div class="card-content">
        
        <div class="timeout-overview">
          <p>Teams are allowed <strong>3 timeouts per match</strong> that can be used at any time during the game.</p>
        </div>
        
        <div class="timeout-rules">
          <div class="timeout-rule">
            <Icon name="mdi:hand-back-right" />
            <div class="rule-text">
              <h4>Initiating Timeouts</h4>
              <p>A player or team member calls timeout. If a team member suggests it, the player may refuse but the timeout is still considered used.</p>
            </div>
          </div>
          
          <div class="timeout-rule">
            <Icon name="mdi:coin" />
            <div class="rule-text">
              <h4>Timeout Token</h4>
              <p>The person giving timeout hands the opposing team a coin or glass bead to indicate the timeout was used.</p>
            </div>
          </div>
          
          <div class="timeout-rule">
            <Icon name="mdi:clock-outline" />
            <div class="rule-text">
              <h4>Time Limit</h4>
              <p>Timeouts are limited to <strong>1 minute</strong> maximum duration.</p>
            </div>
          </div>
          
          <div class="timeout-rule">
            <Icon name="mdi:ruler" />
            <div class="rule-text">
              <h4>Coaching Guidelines</h4>
              <p>Coaches may use equipment or body for measuring/instruction. Legal measuring methods like the two-finger method are permitted. Cannot touch live cue ball or mark the table.</p>
            </div>
          </div>
        </div>
        
      </div>
    </div>

    <!-- Bathroom / Smoke Break -->
    <div class="format-card break-card">
      <div class="card-header">
        <Icon name="mdi:coffee" />
        <h3>Bathroom / Smoke Breaks</h3>
      </div>
      <div class="card-content">
        <div class="break-rule">
          <Icon name="mdi:information" />
          <p>Breaks can <strong>only be taken between games</strong>, not during an active game.</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
// No script needed for this informational component
</script>

<style scoped>
.format-content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 32px;
}

.overview-card {
  grid-column: 1 / -1;
}

/* Format Cards */
.format-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.format-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.format-card:hover {
  transform: translateY(-8px);
  border-color: rgba(255, 184, 28, 0.4);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.format-card:hover::before {
  opacity: 1;
}

/* Card Headers */
.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px 16px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
  position: relative;
}

.card-header svg {
  font-size: 2rem;
  color: var(--gold);
  flex-shrink: 0;
}

.card-header h3 {
  color: var(--gold);
  font-size: 1.6rem;
  font-family: var(--font-heading);
  font-weight: 600;
  margin: 0;
  flex: 1;
}

/* Badges */
.format-badge,
.timeout-badge {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(255, 184, 28, 0.3);
}

/* Card Content */
.card-content {
  padding: 24px 32px 32px;
}

.card-content p {
  color: var(--cream);
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 1rem;
}

.card-content p:last-child {
  margin-bottom: 0;
}

/* Highlight Box */
.highlight-box {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(255, 184, 28, 0.1), rgba(255, 184, 28, 0.05));
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
}

.highlight-box svg {
  font-size: 2.5rem;
  color: var(--gold);
  flex-shrink: 0;
}

.highlight-box h4 {
  color: var(--gold);
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.highlight-box p {
  margin: 0;
  font-size: 1.1rem;
}

/* Format Subsections */
.format-subsection {
  margin-bottom: 32px;
}

.format-subsection:last-child {
  margin-bottom: 0;
}

.subsection-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
}

.subsection-header svg {
  font-size: 1.4rem;
  color: var(--gold);
}

.subsection-header h4 {
  color: var(--gold);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

/* Rules Grid */
.rules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 184, 28, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.rule-item:hover {
  background: rgba(255, 184, 28, 0.08);
  border-color: rgba(255, 184, 28, 0.2);
  transform: translateY(-2px);
}

.rule-item svg {
  font-size: 1.8rem;
  color: var(--gold);
  flex-shrink: 0;
  margin-top: 2px;
}

.rule-content h4 {
  color: var(--gold);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.rule-content p {
  color: var(--cream);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  opacity: 0.9;
}

/* Scoring System Styles */
.scoring-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  margin-bottom: 24px;
}

.scoring-info svg {
  font-size: 1.5rem;
  color: var(--sky-blue);
}

.scoring-info p {
  margin: 0;
  color: var(--cream);
}

.scoring-types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.scoring-type {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  overflow: hidden;
}

.type-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 184, 28, 0.1);
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
}

.type-header svg {
  font-size: 1.5rem;
  color: var(--gold);
}

.type-header h4 {
  color: var(--gold);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.scoring-rules {
  padding: 20px;
}

.score-rule {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 184, 28, 0.05);
  border-radius: 8px;
}

.score-rule:last-child {
  margin-bottom: 0;
}

.points {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
}

.description {
  color: var(--cream);
  font-size: 0.95rem;
  line-height: 1.5;
}

.rules-note {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
}

.rules-note svg {
  font-size: 1.3rem;
  color: var(--sky-blue);
  flex-shrink: 0;
  margin-top: 2px;
}

.rules-note p {
  margin: 0;
  font-size: 0.95rem;
}

/* Timeout Styles */
.timeout-overview {
  margin-bottom: 24px;
  padding: 20px;
  background: rgba(255, 184, 28, 0.08);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.2);
}

.timeout-overview p {
  margin: 0;
  font-size: 1.1rem;
  text-align: center;
}

.timeout-rules {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.timeout-rule {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.timeout-rule:hover {
  background: rgba(255, 184, 28, 0.05);
  border-color: rgba(255, 184, 28, 0.2);
}

.timeout-rule svg {
  font-size: 1.8rem;
  color: var(--gold);
  flex-shrink: 0;
  margin-top: 2px;
}

.rule-text h4 {
  color: var(--gold);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.rule-text p {
  color: var(--cream);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  opacity: 0.9;
}

/* Break Rule */
.break-rule {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: rgba(255, 184, 28, 0.08);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  text-align: center;
}

.break-rule svg {
  font-size: 2rem;
  color: var(--gold);
  flex-shrink: 0;
}

.break-rule p {
  margin: 0;
  font-size: 1.1rem;
  color: var(--cream);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .format-content-grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 28px;
  }
}

@media (max-width: 768px) {
  .format-content-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  .rules-grid,
  .scoring-types {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .card-header,
  .card-content {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .highlight-box {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .break-rule {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .card-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .format-badge,
  .timeout-badge {
    order: -1;
  }
  
  .rule-item,
  .timeout-rule {
    padding: 16px;
  }
  
  .score-rule {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
